import { reaction } from 'mobx';
import { observer, useLocalObservable } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ICampaignPartnerStats, TAttributionModel } from '@feathr/blackbox';
import { reportModuleLabels } from '@feathr/blackbox';
import type { TCollectionTableSort } from '@feathr/components';
import { Alert, Button, Spinner, TableStatsCard, TanStackTable } from '@feathr/components';
import { useToggle } from '@feathr/hooks';
import { useStore } from '@feathr/report_components/state';

import ExportModal from '../ExportModal';
import partnersStatsColumns from './PartnersStatsColumns';

import * as styles from './PartnersStatsTableCard.css';

interface IProps {
  start: string;
  end: string;
  campaignId: string;
  attributionModel: TAttributionModel;
  localUrl: ((url: string) => string) | undefined;
}

function PartnersStatsTableCard({
  start,
  end,
  campaignId,
  attributionModel,
  localUrl,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { Campaigns, Partners } = useStore();
  const [showExportModal, toggleExportModal] = useToggle(false);
  const [isPartial, setIsPartial] = useState(false);

  const state = useLocalObservable(() => ({
    items: [] as ICampaignPartnerStats[],
    setItems: (items): void => {
      state.items = items;
    },
    sort: [{ id: 'conversions', desc: true }] as TCollectionTableSort,
    // A new ordering invalidates the previous page's row positions.
    setSort: (sort: TCollectionTableSort): void => {
      state.sort = sort;
      state.pagination.page = 0;
    },
    pagination: { page: 0, page_size: 20 },
    setPage: (page: number): void => {
      state.pagination.page = page;
    },
    setPageSize: (pageSize: number, page: number): void => {
      state.pagination.page_size = pageSize;
      state.pagination.page = page;
    },
    totalCount: 0,
    setTotalCount: (count: number): void => {
      state.totalCount = count;
    },
  }));

  async function getItems(): Promise<void> {
    const response = await Campaigns.getPartnerStats(
      campaignId,
      start,
      end,
      attributionModel,
      state.sort,
      state.pagination,
    );
    state.setItems(response.data);
    if (response.meta?.is_partial) {
      setIsPartial(true);
    }
    if (response.meta?.page?.count !== undefined) {
      state.setTotalCount(response.meta.page.count);
    }
  }

  useEffect(() => {
    getItems().catch((error) => {
      throw error;
    });
  }, []);

  useEffect(() =>
    reaction(
      () => [state.pagination.page, state.pagination.page_size, state.sort],
      () => {
        getItems();
      },
    ),
  );

  if (!state.items) {
    return <Spinner />;
  }

  async function handleExportPartnerStats(email): Promise<void> {
    Campaigns.exportPartnerStats(
      campaignId,
      start,
      end,
      attributionModel,
      state.sort,
      state.pagination,
      email,
    );
  }

  const pagination = state.pagination;

  const actions = [
    <Button key={'export'} onClick={toggleExportModal}>
      {t('Export to CSV')}
    </Button>,
  ];

  return (
    <>
      <TableStatsCard actions={actions} title={reportModuleLabels.includePartnersStatsTable}>
        {isPartial && (
          <Alert
            className={styles.alert}
            title={t('Your campaign has more partners than can be shown in this table.')}
            type={'warning'}
          />
        )}
        <TanStackTable<ICampaignPartnerStats>
          columns={partnersStatsColumns({
            localUrl,
            pagination,
            partners: Partners,
            sort: state.sort,
            t,
            totalCount: state.totalCount,
          })}
          idKey={'p_id'}
          items={state.items}
          onPageChange={state.setPage}
          onPageSizeChange={state.setPageSize}
          onSortChange={state.setSort}
          page={state.pagination.page}
          pages={
            state.totalCount > 0
              ? Math.ceil(state.totalCount / state.pagination.page_size)
              : undefined
          }
          pageSize={state.pagination.page_size}
          sort={state.sort}
        />
      </TableStatsCard>
      {showExportModal && (
        <ExportModal
          onClose={toggleExportModal}
          onConfirm={handleExportPartnerStats}
          title={t('Export Partner Stats')}
        />
      )}
    </>
  );
}

export default observer(PartnersStatsTableCard);
